import { type ReactNode, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type FieldPath, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { EmailField } from "@/components/ui/email-field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { emailSchema } from "@/lib/validation";

import {
  EXTERNAL_USER_TYPE_LABEL_KEYS,
  EXTERNAL_USER_TYPES,
  GENERIC_ORGANIZATION_LABEL_KEY,
  ORGANIZATION_LABEL_KEYS,
} from "../../types";

export const EXTERNAL_USER_FORM_ID = "external-user-form";

const createExternalUserSchema = (requiredMessage: string, invalidEmailMessage: string) =>
  z.object({
    first_name: z.string().trim().min(1, { message: requiredMessage }),
    last_name: z.string().trim().min(1, { message: requiredMessage }),
    email: emailSchema({ required: requiredMessage, invalid: invalidEmailMessage }),
    type: z.enum(EXTERNAL_USER_TYPES, { message: requiredMessage }),
    organization: z.string().trim().min(1, { message: requiredMessage }),
  });

export type ExternalUserFormValues = z.infer<ReturnType<typeof createExternalUserSchema>>;

interface ExternalUserFormProps {
  onValidate: (values: ExternalUserFormValues) => void;
  fieldErrors?: { data?: Record<string, unknown> };
  initialValues?: Partial<ExternalUserFormValues>;
}

interface FieldShellProps {
  id: string;
  label: string;
  children: ReactNode;
  errorMessage?: string;
  required?: boolean;
  className?: string;
}

const toErrorMessage = (error: unknown): string => {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  if (Array.isArray(error)) {
    return error.join(", ");
  }

  if (typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }

  return String(error);
};

const FieldShell = ({
  id,
  label,
  children,
  errorMessage,
  required = false,
  className,
}: FieldShellProps) => (
  <div className={cn("space-y-2", className)}>
    <Label htmlFor={id} className="font-semibold text-foreground">
      {label}
      {required && (
        <span aria-hidden="true" className="ml-0.5 text-destructive">
          *
        </span>
      )}
    </Label>
    {children}
    {errorMessage && (
      <p id={`${id}-error`} role="alert" className="text-destructive text-sm">
        {errorMessage}
      </p>
    )}
  </div>
);

export const ExternalUserForm = ({
  onValidate,
  fieldErrors = {},
  initialValues,
}: ExternalUserFormProps) => {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      createExternalUserSchema(
        t("traderDetails.form.mandatoryFieldBlankError"),
        t("traderDetails.form.invalidEmailError")
      ),
    [t]
  );

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ExternalUserFormValues>({
    defaultValues: {
      first_name: initialValues?.first_name ?? "",
      last_name: initialValues?.last_name ?? "",
      email: initialValues?.email ?? "",
      type: initialValues?.type,
      organization: initialValues?.organization ?? "",
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
  });

  const serverErrors = fieldErrors.data;

  useEffect(() => {
    clearErrors();

    if (!serverErrors || Object.keys(serverErrors).length === 0) {
      return;
    }

    Object.entries(serverErrors).forEach(([fieldName, message]) => {
      setError(fieldName as FieldPath<ExternalUserFormValues>, {
        type: "server",
        message: toErrorMessage(message),
      });
    });
  }, [clearErrors, serverErrors, setError]);

  const getFieldError = (fieldName: keyof ExternalUserFormValues) =>
    toErrorMessage(errors[fieldName]?.message);

  const selectedType = useWatch({ control, name: "type" });
  const organizationLabel = t(
    selectedType ? ORGANIZATION_LABEL_KEYS[selectedType] : GENERIC_ORGANIZATION_LABEL_KEY
  );

  return (
    // min-w-0: the dialog is a grid, so without it a wide field stretches the whole panel
    <form
      id={EXTERNAL_USER_FORM_ID}
      noValidate
      className="min-w-0 space-y-5"
      onSubmit={handleSubmit(onValidate)}
    >
      <Controller
        control={control}
        name="first_name"
        render={({ field }) => {
          const error = getFieldError("first_name");

          return (
            <FieldShell
              id="first_name"
              label={t("managerDetails.firstName")}
              errorMessage={error}
              required
            >
              <Input
                {...field}
                id="first_name"
                aria-invalid={!!error}
                aria-describedby={error ? "first_name-error" : undefined}
                className="border-border"
              />
            </FieldShell>
          );
        }}
      />

      <Controller
        control={control}
        name="last_name"
        render={({ field }) => {
          const error = getFieldError("last_name");

          return (
            <FieldShell
              id="last_name"
              label={t("managerDetails.lastName")}
              errorMessage={error}
              required
            >
              <Input
                {...field}
                id="last_name"
                aria-invalid={!!error}
                aria-describedby={error ? "last_name-error" : undefined}
                className="border-border"
              />
            </FieldShell>
          );
        }}
      />

      <EmailField control={control} name="email" label={t("managerDetails.email")} required />

      <Controller
        control={control}
        name="type"
        render={({ field }) => {
          const error = getFieldError("type");

          return (
            <FieldShell
              id="type"
              label={t("externalUserDetails.typeLabel")}
              errorMessage={error}
              required
            >
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  id="type"
                  aria-invalid={!!error}
                  aria-describedby={error ? "type-error" : undefined}
                  onBlur={field.onBlur}
                  className="h-10 w-full border-border"
                >
                  <SelectValue placeholder={t("externalUserDetails.typePlaceholder")} />
                </SelectTrigger>
                <SelectContent position="popper" className="max-h-72">
                  {EXTERNAL_USER_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      {t(EXTERNAL_USER_TYPE_LABEL_KEYS[type])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldShell>
          );
        }}
      />

      <Controller
        control={control}
        name="organization"
        render={({ field }) => {
          const error = getFieldError("organization");

          return (
            <FieldShell id="organization" label={organizationLabel} errorMessage={error} required>
              <Input
                {...field}
                id="organization"
                aria-invalid={!!error}
                aria-describedby={error ? "organization-error" : undefined}
                className="border-border"
              />
            </FieldShell>
          );
        }}
      />
    </form>
  );
};
